import type { NurseResponse } from "@/types/nurse-type";

type PrecepteeConfigLike = {
  preceptee_on?: boolean | null;
  preceptee_shift_count?: boolean | null;
} | null;

export type PrecepteePolicy = {
  isPrecepteeFeatureEnabled: boolean;
  includePrecepteeInCount: boolean;
  excludePrecepteeFromCount: boolean;
  excludePrecepteeFromUi: boolean;
};

/**
 * 프리셉티 정책 해석 단일화:
 * - preceptee_shift_count=true  => 합산 포함(Y)
 * - preceptee_shift_count=false => 합산 제외(N)
 */
export function getPrecepteePolicy(
  config?: PrecepteeConfigLike
): PrecepteePolicy {
  const hasCountPolicy = config?.preceptee_shift_count != null;
  const includePrecepteeInCount = hasCountPolicy
    ? Boolean(config?.preceptee_shift_count)
    : true;
  const excludePrecepteeFromCount = !includePrecepteeInCount;

  return {
    isPrecepteeFeatureEnabled: Boolean(config?.preceptee_on),
    includePrecepteeInCount,
    excludePrecepteeFromCount,
    // 현재 UI 규칙은 합산 정책과 동일하게 적용
    excludePrecepteeFromUi: excludePrecepteeFromCount,
  };
}

export type PrecepteeAssignmentLike = {
  nurse_id: string;
  /** 있으면 '프리셉티'만 채택. period SSOT item 은 reason 없음(전부 프리셉티). */
  reason?: string;
  preceptor_id?: string | null;
  start_date: string;
  expected_end_date?: string | null;
  end_date?: string | null;
  status?: string | null;
};

/**
 * 프리셉티 여부를 캐시(preceptor_id)가 아니라 **assignment 기간(period)** 으로 판정한다.
 *
 * 대상 월(year/month) 안에서 각 간호사가 프리셉티인 **일(1-based day)** 집합을 반환.
 * - reason='프리셉티' assignment 의 [start_date, end] 와 그 달의 교집합
 * - end = end_date ?? expected_end_date (inclusive). 둘 다 없으면 무기한(그 달 말일까지)
 * - status='cancelled' 는 제외
 *
 * 이 day 집합으로 카운트에서 **그 날의 셀만** 빼면 시점 관리가 데이터대로 동작한다:
 *  - 종료월(다음달) → 교집합 없음 → 집합 비어 → 전일 카운트 (끝난 뒤 정상 카운트)
 *  - 월 중간 종료(예: 7/14) → {1..14} → 그 날만 제외, 15일~ 정상 카운트
 *  - 무기한 → 그 달 전체 → 계속 제외
 */
export function getPrecepteeDaysByNurseId(
  assignments: PrecepteeAssignmentLike[] | undefined,
  year?: number | null,
  month?: number | null
): Map<string, Set<number>> {
  const map = new Map<string, Set<number>>();
  if (!assignments || year == null || month == null) return map;
  const daysInMonth = new Date(year, month, 0).getDate();
  const monthStart = new Date(year, month - 1, 1);
  const monthEnd = new Date(year, month, 0);
  for (const a of assignments) {
    if (a.reason && a.reason !== "프리셉티") continue; // reason 있으면 프리셉티만
    if (a.status === "cancelled") continue;
    const start = new Date(a.start_date);
    const endRaw = a.end_date ?? a.expected_end_date ?? null;
    const end = endRaw ? new Date(endRaw) : null; // null = 무기한
    // 그 달과 겹치지 않으면 스킵
    if (!(start <= monthEnd && (end == null || end >= monthStart))) continue;
    const sDay = start > monthStart ? start.getDate() : 1;
    const eDay = end != null && end < monthEnd ? end.getDate() : daysInMonth;
    let set = map.get(a.nurse_id);
    if (!set) {
      set = new Set<number>();
      map.set(a.nurse_id, set);
    }
    for (let d = sDay; d <= eDay; d++) set.add(d);
  }
  return map;
}

export function applyPrecepteePeriodsToNurses(
  nurses: readonly NurseResponse[] | undefined,
  periods: readonly PrecepteeAssignmentLike[] | undefined
): NurseResponse[] {
  const source = nurses ?? [];
  if (!periods) return [...source];

  const preceptorIdByPrecepteeId = new Map(
    periods
      .filter((period) => period.preceptor_id)
      .map((period) => [period.nurse_id, period.preceptor_id] as const)
  );

  return source.map((nurse) => ({
    ...nurse,
    preceptor_id: preceptorIdByPrecepteeId.get(nurse.nurse_id) ?? null,
  }));
}
